import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/apiClient.ts';
import { RefreshCw, Calendar, PlusCircle, X, Printer } from 'lucide-react'; // NEW
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ReceiptModal from '../common/ReceiptModal';
import TableSearch from '../common/TableSearch';
import { filterRows } from '../../utils/tableSearch';
import { formatDate } from '../../utils/date';
import DateInput from '../common/DateInput';

/**
 * Balancestr.jsx — صفحة "أرصدة المجموعات"
 * - تحميل أولي بدون تاريخ
 * - فلترة بتاريخ من/إلى بصيغة YYYY-MM-DD
 * - جدول بخمسة أعمدة: (اسم المجموعة، سعر المجموعة، واصل نقداً، الأقساط المستلمة، الرصيد المتبقي)
 * - فوتر يحوي المجاميع
 * - يحافظ على الثيم العام (Tailwind + dark mode)
 */

const API_URL = '/v1/reports/traveler-balances';
// داخل apiClient.ts قبل export default api;
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // 👇 أضف هذا السطر
  const tenant = localStorage.getItem("tenant") || "default-tenant-1";
  config.headers["X-Tenant"] = tenant;

  const branch = localStorage.getItem("branch_id");
  if (branch) config.headers["X-Branch"] = branch;
  
  return config;
});

// 🔧 تاريخ فقط → "YYYY-MM-DD 00:00:00"
const toSqlDateOnly = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s + ' 00:00:00';
  return s.replace('T', ' ') + ':00';
};

// تنسيق المبلغ بفواصل الآلاف
const formatWithCommas = (val) => {
  if (val === '' || val === null || val === undefined) return '';
  const str = String(val).replace(/,/g, '');
  const num = parseFloat(str);
  if (isNaN(num)) return str;
  const parts = num.toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts[1] === '00' ? parts[0] : parts.join('.');
};

const parseFormattedNumber = (str) => {
  if (!str || str === '') return '';
  const cleaned = String(str).replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? '' : num;
};

const Balancestravelers = () => {
  const { t } = useTranslation?.() ?? { t: (k) => k };

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [balanceTypeFilter, setBalanceTypeFilter] = useState('all'); // all | installments | deferred
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

    // NEW: حالة المودال + حقول الإدخال
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [dt, setDt] = useState(''); // datetime-local
  const [saving, setSaving] = useState(false);

  // وصل الاستلام — يُبنى بعد نجاح الحفظ ثم يُعرض للمعاينة والطباعة
  const [receipt, setReceipt] = useState(null);

  // سجل سندات المسافر — لإعادة طباعة أي سند سابق
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRow, setHistoryRow] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);


  // محاولة ذكية: جرّب GET أولاً، وإذا الـ API يطلب POST فقط، نحاول POST تلقائيًا.
  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (from) params.df = from;
      if (to) params.dt = to;
      if (balanceTypeFilter && balanceTypeFilter !== 'all') params.balance_type = balanceTypeFilter;

      let resp;
      try {
        resp = await api.get(API_URL, { params });
      } catch (e) {
        resp = await api.post(API_URL, {
          df: from || null,
          dt: to || null,
          balance_type: balanceTypeFilter !== 'all' ? balanceTypeFilter : null,
        });
      }
      const data = resp?.data?.data ?? resp?.data ?? [];
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Network Error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceTypeFilter]);

  // إخفاء الصفوف التي رصيدها صفر
  const visibleRows = useMemo(() => rows.filter(r => Number(r?.balance ?? 0) !== 0), [rows]);

  // ثم تطبيق البحث فوقها؛ المجاميع تُحسب من النتيجة النهائية
  // حتى يبقى مجموع الفوتر مطابقاً للصفوف المعروضة
  const displayRows = useMemo(
    () => filterRows(visibleRows, search, ['traveler_name', 'group_name']),
    [visibleRows, search]
  );

  const totals = useMemo(() => {
    const agg = { total_price: 0, cash_received: 0, installments_paid: 0, balance: 0 };
    for (const r of displayRows) {
      agg.total_price       += Number(r?.total_price ?? 0);
      agg.cash_received     += Number(r?.cash_received ?? 0);
      agg.installments_paid += Number(r?.installments_paid ?? 0);
      agg.balance           += Number(r?.balance ?? 0);
    }
    return agg;
  }, [displayRows]);

    const isDeferred = selectedRow?.balance_type === 'deferred';
  const remainingBalance = Number(selectedRow?.balance ?? 0);

  const openActionModal = (row) => {
    setSelectedRow(row);
    setAmount('');
    setNotes('');
    setDt('');
    setModalOpen(true);
  };

  /**
   * يبني وصل الاستلام. يخدم حالتين:
   *  - سند جديد فور الحفظ (paidBefore = الأقساط المسجّلة قبله)
   *  - إعادة طباعة سند قديم (paidBefore = مجموع الأقساط السابقة له زمنياً)
   *
   * الرصيد المعروض هو رصيد الحساب لحظة ذلك الاستلام لا الرصيد الحالي،
   * لأن السند وثيقة تاريخية يجب أن تبقى مطابقة لما وُقّع عليه.
   *
   * @param {object}  args
   * @param {'installment'|'deferred'} args.kind
   * @param {object}  args.row         صف الرصيد (سعر المجموعة والواصل نقداً)
   * @param {number}  args.paid        المبلغ المستلم بهذا السند
   * @param {number}  args.paidBefore  مجموع الأقساط المستلمة قبل هذا السند
   * @param {*}       args.serialId    معرّف السجل
   * @param {string}  args.date        تاريخ العملية
   * @param {string}  [args.notes]     ملاحظات السند
   */
  const buildTravelerReceipt = ({ kind, row, paid, paidBefore, serialId, date, notes: receiptNotes }) => {
    const isDeferredKind = kind === 'deferred';
    const totalPrice = Number(row?.total_price ?? 0);
    const cashReceived = Number(row?.cash_received ?? 0);
    const priorPaid = Number(paidBefore ?? 0);
    // نفس معادلة الخادم: الرصيد = السعر − النقد − مجموع الأقساط حتى هذا السند
    const balanceAfter = totalPrice - cashReceived - priorPaid - paid;
    const prefix = isDeferredKind ? 'DEF' : 'INS';

    return {
      direction: 'in',
      docTitle: 'سند قبض',
      subtitle: isDeferredKind ? 'استلام مبلغ من رصيد آجل' : 'استلام قسط',
      serial: serialId ? `${prefix}-${String(serialId).padStart(6, '0')}` : `${prefix}-—`,
      date,
      partyLabel: 'استلمنا من السيد/السيدة',
      partyName: row?.traveler_name || '—',
      details: [
        { label: 'المجموعة', value: row?.group_name || '—' },
        { label: 'نوع الرصيد', value: isDeferredKind ? 'آجل' : 'أقساط' },
      ],
      amount: paid,
      summary: [
        { label: 'سعر المجموعة الكلي', value: totalPrice },
        { label: 'المدفوع نقداً عند التعاقد', value: cashReceived },
        { label: 'الأقساط المستلمة سابقاً', value: priorPaid },
        { label: isDeferredKind ? 'المبلغ المستلم بهذا السند' : 'القسط المستلم بهذا السند', value: paid, tone: 'accent' },
        { label: 'الرصيد المتبقي بعد الاستلام', value: balanceAfter, tone: 'due' },
      ],
      notes: receiptNotes,
    };
  };

  /** يفتح قائمة سندات المسافر في هذه المجموعة لإعادة طباعة أي منها */
  const openHistory = async (row) => {
    setHistoryRow(row);
    setHistoryOpen(true);
    setHistoryRows([]);
    setHistoryLoading(true);
    try {
      const grId = row.gr_id ?? row.GroupID;
      const resp = await api.get('/v1/installments', {
        params: { gr_id: grId, tr_id: row.traveler_id, per_page: 200, sort: 'created_at', dir: 'asc' },
      });
      const data = resp?.data?.data ?? resp?.data ?? [];
      setHistoryRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || 'تعذّر جلب السندات');
    } finally {
      setHistoryLoading(false);
    }
  };

  /**
   * يطبع سنداً من السجل. الأقساط مرتّبة تصاعدياً زمنياً، فمجموع ما قبل
   * الفهرس idx هو "المستلم سابقاً" لحظة إصدار ذلك السند.
   */
  const printHistoryEntry = (entry, idx) => {
    const priorPaid = historyRows
      .slice(0, idx)
      .reduce((sum, r) => sum + Number(r.amount || 0), 0);

    setReceipt(buildTravelerReceipt({
      kind: historyRow?.balance_type === 'deferred' ? 'deferred' : 'installment',
      row: historyRow,
      paid: Number(entry.amount || 0),
      paidBefore: priorPaid,
      serialId: entry.id,
      // التطبيق يحفظ تاريخ العملية الذي يختاره المستخدم في updated_at
      date: entry.updated_at || entry.created_at,
      notes: entry.notes,
    }));
    setHistoryOpen(false);
  };

  const saveInstallment = async () => {
    if (!selectedRow) return;
    const branchId = localStorage.getItem('branch_id') || '1';
    const tenant   = localStorage.getItem('tenant') || 'default-tenant-1';
    const grId = selectedRow.gr_id ?? selectedRow.GroupID;
    const trId = selectedRow.traveler_id;

    if (!grId || !trId) {
      toast.error('المعرّفات ناقصة (gr_id / tr_id).');
      return;
    }
    if (!amount || Number(amount) <= 0) {
      toast.error('أدخل مبلغ صحيح.');
      return;
    }
    if (!dt) {
      toast.error('اختر التاريخ.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        gr_id: grId,
        tr_id: trId,
        vault: Number(branchId),
        amount: Number(amount),
        notes: notes || null,
        branch_id: Number(branchId),
        updated_at: toSqlDateOnly(dt),
      };
      const resp = await api.post('/v1/installments', payload, {
        headers: { 'X-Tenant': tenant, 'X-Branch': branchId },
      });
      const createdId = resp?.data?.data?.id ?? resp?.data?.id;
      setReceipt(buildTravelerReceipt({
        kind: 'installment',
        row: selectedRow,
        paid: Number(amount),
        paidBefore: Number(selectedRow?.installments_paid ?? 0),
        serialId: createdId,
        date: dt,
        notes,
      }));
      setModalOpen(false);
      await fetchData();
      toast.success('تم استلام القسط ✨');
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || 'خطأ أثناء الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const saveDeferredAmount = async () => {
    if (!selectedRow) return;
    const branchId = localStorage.getItem('branch_id') || '1';
    const tenant   = localStorage.getItem('tenant') || 'default-tenant-1';
    const grId = selectedRow.gr_id ?? selectedRow.GroupID;
    const trId = selectedRow.traveler_id;

    if (!grId || !trId) {
      toast.error('المعرّفات ناقصة.');
      return;
    }
    const amt = Number(amount);
    if (!amount || amt <= 0) {
      toast.error('أدخل مبلغ صحيح.');
      return;
    }
    if (amt - remainingBalance > 0.01) {
      toast.error('المبلغ المستلم لا يجوز أن يتجاوز الرصيد المتبقي.');
      return;
    }
    if (!dt) {
      toast.error('اختر التاريخ.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        gr_id: grId,
        tr_id: trId,
        vault: Number(branchId),
        amount: amt,
        notes: notes || null,
        branch_id: Number(branchId),
        updated_at: toSqlDateOnly(dt),
      };
      const resp = await api.post('/v1/reports/traveler-balances/receive-deferred', payload, {
        headers: { 'X-Tenant': tenant, 'X-Branch': branchId },
      });
      const createdId = resp?.data?.data?.id ?? resp?.data?.id;
      setReceipt(buildTravelerReceipt({
        kind: 'deferred',
        row: selectedRow,
        paid: amt,
        paidBefore: Number(selectedRow?.installments_paid ?? 0),
        serialId: createdId,
        date: dt,
        notes,
      }));
      setModalOpen(false);
      await fetchData();
      toast.success('تم استلام المبلغ ✨');
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'خطأ أثناء الحفظ';
      toast.error(typeof msg === 'string' ? msg : 'المبلغ يتجاوز الرصيد المتبقي.');
    } finally {
      setSaving(false);
    }
  };

  const handleModalSubmit = () => {
    if (isDeferred) saveDeferredAmount();
    else saveInstallment();
  };

  return (
    <div className="w-full mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-8">
        {t('أرصدة المسافرين') || 'أرصدة المسافرين'}
      </h1>

      {/* فلاتر التاريخ + نوع الرصيد */}
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
          {/* نوع الرصيد */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('نوع الرصيد') || 'نوع الرصيد'}
            </label>
            <select
              value={balanceTypeFilter}
              onChange={(e) => setBalanceTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">{t('الكل') || 'الكل'}</option>
              <option value="installments">{t('الأقساط') || 'الأقساط'}</option>
              <option value="deferred">{t('الآجل') || 'الآجل'}</option>
            </select>
          </div>
          {/* من */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('من تاريخ') || 'مِن تاريخ'}
            </label>
            <div className="relative">
              <DateInput

 value={from}
 onChange={(e) => setFrom(e.target.value)}
 className="w-full pl-10 rtl:pl-3 rtl:pr-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
 />
            </div>
          </div>

          {/* إلى */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('إلى تاريخ') || 'إلى تاريخ'}
            </label>
            <div className="relative">
              <DateInput

 value={to}
 onChange={(e) => setTo(e.target.value)}
 className="w-full pl-10 rtl:pl-3 rtl:pr-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
 />
            </div>
          </div>

          {/* زر تحديث */}
          <div className="flex sm:justify-end">
            <button
              onClick={fetchData}
              disabled={loading}
              className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-medium shadow-sm transition-colors duration-200"
              title={t('تحديث') || 'تحديث'}
            >
              <RefreshCw className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
              {loading ? (t('يتم الجلب...') || 'يتم الجلب...') : (t('تحديث') || 'تحديث')}
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}
      </div>

      {/* الجدول */}
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg">
        <TableSearch
          value={search}
          onChange={setSearch}
          placeholder="ابحث باسم المسافر أو المجموعة..."
          resultCount={displayRows.length}
          totalCount={visibleRows.length}
          className="mb-3"
        />
        <div className="app-table-wrap">
          <table className="app-table min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  {t('اسم المسافر') || 'اسم المسافر'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  {t('اسم المجموعة') || 'اسم المجموعة'}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  {t('سعر المجموعة') || 'سعر المجموعة'}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  {t('واصل نقداً') || 'واصل نقداً'}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  {t('الأقساط المستلمة') || 'الأقساط المستلمة'}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  {t('الرصيد المتبقي') || 'الرصيد المتبقي'}
                </th>
                {/* NEW: عمود إجراء */}
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  {t('إجراء') || 'إجراء'}
                </th>                
              </tr>
            </thead>

            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-6 text-center text-gray-500 dark:text-gray-400">
                    {t('يتم الجلب...') || 'يتم الجلب...'}
                  </td>
                </tr>
              ) : displayRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-6 text-center text-gray-500 dark:text-gray-400">
                    {t('لا توجد بيانات') || 'لا توجد بيانات'}
                  </td>
                </tr>
              ) : (
                displayRows.map((r) => (  
                  <tr
                    key={`${r.traveler_id}-${r.gr_id ?? r.GroupID}-${r.balance_type ?? 'installments'}`}
                    data-balance-type={r.balance_type ?? 'installments'}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors duration-150"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {r.traveler_name}
                    </td>                    
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {r.group_name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {Number(r.total_price || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                      {Number(r.cash_received || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                      {Number(r.installments_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-right text-sm font-bold ${
                      Number(r.balance || 0) > 0
                        ? 'text-orange-700 dark:text-orange-400'
                        : Number(r.balance || 0) === 0
                        ? 'text-gray-600 dark:text-gray-300'
                        : 'text-red-700 dark:text-red-400'
                    }`}>
                      {Number(r.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {r.balance_type === 'deferred' ? (
                          <button
                            onClick={() => openActionModal(r)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium shadow"
                            title="استلام مبلغ"
                          >
                            <PlusCircle className="w-4 h-4" />
                            استلام مبلغ
                          </button>
                        ) : (
                          <button
                            onClick={() => openActionModal(r)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium shadow"
                            title="استلام قسط"
                          >
                            <PlusCircle className="w-4 h-4" />
                            استلام قسط
                          </button>
                        )}
                        <button
                          onClick={() => openHistory(r)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 text-xs font-medium"
                          title="عرض سندات المسافر وإعادة طباعتها"
                        >
                          <Printer className="w-4 h-4" />
                          السندات
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {/* فوتر المجاميع */}
            <tfoot className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <td className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200">
                  {t('المجاميع') || 'المجاميع'}
                </td>
                <td className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200">
                </td>                
                <td className="px-4 py-3 text-right text-sm font-extrabold text-gray-900 dark:text-gray-100">
                  {totals.total_price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                </td>
                <td className="px-4 py-3 text-right text-sm font-extrabold text-emerald-700 dark:text-emerald-400">
                  {totals.cash_received.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                </td>
                <td className="px-4 py-3 text-right text-sm font-extrabold text-emerald-700 dark:text-emerald-400">
                  {totals.installments_paid.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                </td>
                <td className={`px-4 py-3 text-right text-sm font-extrabold ${
                  totals.balance > 0
                    ? 'text-orange-700 dark:text-orange-400'
                    : totals.balance === 0
                    ? 'text-gray-600 dark:text-gray-300'
                    : 'text-red-700 dark:text-red-400'
                }`}>
                  {totals.balance.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                </td>
                <td className="px-4 py-3" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* مودال استلام قسط (أقساط) أو استلام مبلغ (آجل) */}
      {modalOpen && selectedRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {isDeferred ? (t('استلام مبلغ') || 'استلام مبلغ') : (t('استلام قسط') || 'استلام قسط')}
              </h3>
              <button onClick={() => setModalOpen(false)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>

            <div className="px-4 py-4 space-y-4">
              {isDeferred && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('الرصيد المتبقي') || 'الرصيد المتبقي'}: <strong>{remainingBalance.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع</strong>
                  {' — '}{t('المبلغ لا يتجاوز الرصيد') || 'المبلغ لا يتجاوز الرصيد.'}
                </p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('التاريخ') || 'التاريخ'}
                </label>
                <DateInput

 value={dt}
 onChange={(e) => setDt(e.target.value)}
 className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
 />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('المبلغ') || 'المبلغ'}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formatWithCommas(amount)}
                  onChange={(e) => {
                    const parsed = parseFormattedNumber(e.target.value);
                    setAmount(parsed === '' ? '' : parsed);
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-right"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('ملاحظات') || 'ملاحظات'}
                </label>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={isDeferred ? '' : 'مثال: الدفعة الأولى'}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t dark:border-gray-700">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100"
              >
                {t('إلغاء') || 'إلغاء'}
              </button>
              <button
                onClick={handleModalSubmit}
                disabled={saving}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold disabled:opacity-70 ${
                  isDeferred ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                <PlusCircle className="w-4 h-4" />
                {saving ? (t('جاري الحفظ...') || 'جاري الحفظ...') : isDeferred ? (t('حفظ المبلغ') || 'حفظ المبلغ') : (t('حفظ القسط') || 'حفظ القسط')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* سجل سندات المسافر — اختر سنداً لإعادة طباعته */}
      {historyOpen && historyRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
            <div className="flex items-start justify-between px-4 py-3 border-b dark:border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">سندات المسافر</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {historyRow.traveler_name} — {historyRow.group_name}
                </p>
              </div>
              <button onClick={() => setHistoryOpen(false)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto px-4 py-3">
              {historyLoading ? (
                <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">يتم الجلب...</p>
              ) : historyRows.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  لا توجد سندات مسجّلة لهذا المسافر في هذه المجموعة.
                </p>
              ) : (
                <table className="app-table min-w-full">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-right">#</th>
                      <th className="px-3 py-2 text-right">رقم السند</th>
                      <th className="px-3 py-2 text-right">التاريخ</th>
                      <th className="px-3 py-2 text-right">المبلغ</th>
                      <th className="px-3 py-2 text-right">الملاحظات</th>
                      <th className="px-3 py-2 text-center">طباعة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows.map((entry, idx) => (
                      <tr key={entry.id}>
                        <td className="px-3 py-2">{idx + 1}</td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {historyRow.balance_type === 'deferred' ? 'DEF' : 'INS'}-{String(entry.id).padStart(6, '0')}
                        </td>
                        <td className="px-3 py-2">
                          {formatDate(entry.updated_at || entry.created_at, '—')}
                        </td>
                        <td className="px-3 py-2 font-semibold text-emerald-700 dark:text-emerald-400">
                          {Number(entry.amount || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} د.ع
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{entry.notes || '—'}</td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => printHistoryEntry(entry, idx)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium shadow"
                          >
                            <Printer className="w-4 h-4" />
                            وصل
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex justify-end px-4 py-3 border-t dark:border-gray-700">
              <button
                onClick={() => setHistoryOpen(false)}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}

      {/* معاينة وصل الاستلام قبل الطباعة */}
      <ReceiptModal
        open={!!receipt}
        receipt={receipt}
        onClose={() => setReceipt(null)}
      />
    </div>
  );
};

export default Balancestravelers;
